import {
    actions,
    ActionTypes,
    assign,
    createMachine,
    DoneInvokeEvent,
    EventObject,
    LogAction,
    send,
    sendParent,
} from "xstate"
import { Materialized } from "@silversea-ssc/functional-support"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import { PathReporter } from "io-ts/PathReporter"
import { Errors } from "io-ts"
import { BadRequestResponse, BadRequestResponseData } from "@ssc-booking/api-contracts"
import { assertEvent } from "../../xstate-helpers"
import { PerfTrackingData, trackApiPerf } from "../../../v3/_common/tracking"
import { elapsedInMs } from "../../timing"
import {
    DataLoadContext,
    DataLoadEvents,
    DataLoadStateMachine,
    dataLoadTypes,
    LoadError,
    LoadFatalError,
    LoadSuccess,
} from "./types"

export type RequestResponseChannel = {
    sendAndReceive(message: Record<string, unknown>): Promise<Record<string, unknown>>
}

const parseErrorEvent =
    (event: DoneInvokeEvent<unknown>) =>
    (error: Errors): LoadFatalError => ({
        type: "LOAD_FATAL_ERROR",
        errorCode: "parsing",
        message: PathReporter.report(E.left(error)).join("\n"),
        info: event.data,
    })

const unknownResponseTypeEvent = (eventType: string, event: DoneInvokeEvent<unknown>): LoadFatalError => ({
    type: "LOAD_FATAL_ERROR",
    errorCode: "unknownResponse",
    message: `Unknown response type: ${eventType}`,
    info: event.data,
})

type LoadEventWithServerResponseTypes = LoadSuccess<unknown>["type"] | LoadError<unknown>["type"]
const loadEvent =
    (type: LoadEventWithServerResponseTypes) =>
    <T>(parsed: T): LoadSuccess<T> | LoadError<T> => ({
        type,
        response: parsed,
    })
const parseResponse = <E, A>(
    materialized: Materialized<E, A>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: DoneInvokeEvent<any>,
    eventType: LoadEventWithServerResponseTypes,
) => F.pipe(materialized.strictType.decode(event.data.data), E.foldW(parseErrorEvent(event), loadEvent(eventType)))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseBadRequestResponse = (event: DoneInvokeEvent<any>) =>
    F.pipe(
        BadRequestResponseData.strictType.decode(event.data.data),
        E.foldW(parseErrorEvent(event), badRequestResponseEvent(event)),
    )
const badRequestResponseEvent =
    (event: DoneInvokeEvent<unknown>) =>
    (data: BadRequestResponseData): LoadFatalError => ({
        type: "LOAD_FATAL_ERROR",
        errorCode: data.errorCode,
        message: `${BadRequestResponse.strictType.name}: ${data.message}`,
        info: event.data,
    })

export type DataLoadConfig<EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent extends EventObject> = {
    id: string
    channel: RequestResponseChannel
    Request: Materialized<EReq, AReq>
    Success: Materialized<ESuccess, ASuccess>
    successType: string
    Error: Materialized<EError, AError>
    errorType: string
    action: string
    notifyParent: (
        ctx: DataLoadContext<ASuccess, AError>,
        ev: DataLoadEvents<AReq, ASuccess, AError>,
    ) => TParentEvent | undefined
    disableLog?: boolean
}

const invokeSendRequest = {
    src: "sendRequest",
    onDone: {
        actions: "sendResponse",
    },
    onError: {
        actions: "sendFatalError",
    },
}

export const dataLoadMachine = <EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent extends EventObject>(
    config: DataLoadConfig<EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent>,
): DataLoadStateMachine<AReq, ASuccess, AError> => {
    const {
        id,
        channel,
        Request,
        Success,
        successType,
        Error: ErrorM,
        errorType,
        action,
        notifyParent,
        disableLog,
    } = config
    const types = dataLoadTypes(Request, Success, ErrorM)
    type Types = typeof types
    type Context = Types["Context"]
    type Events = Types["Events"]
    type TypeState = Types["TypeState"]
    const logIfEnabled = (logAction: LogAction<Context, Events>) =>
        actions.pure<Context, Events>(() => (disableLog ? undefined : logAction))

    return createMachine<Context, Events, TypeState>(
        {
            id: `data-load-${id}`,
            initial: "idle",
            predictableActionArguments: true,
            context: { id },
            states: {
                idle: {
                    on: {
                        LOAD: { target: "loading.firstLoad", actions: [] },
                    },
                },
                loading: {
                    entry: ["clearError"],

                    states: {
                        firstLoad: {
                            entry: "notifyParent",
                            invoke: invokeSendRequest,
                            on: {
                                LOAD: "firstLoad",
                            },
                        },
                        refresh: {
                            entry: "notifyParent",
                            invoke: invokeSendRequest,
                            on: {
                                LOAD: "refresh",
                            },
                        },
                    },

                    on: {
                        LOAD_SUCCESS: { target: "loaded", actions: ["setResponse"] },
                        LOAD_ERROR: { target: "error", actions: ["setError"] },
                        LOAD_FATAL_ERROR: { target: "error", actions: ["setFatalError"] },
                    },
                },
                loaded: {
                    entry: "notifyParent",
                    on: { LOAD: "loading.refresh" },
                },
                error: {
                    entry: ["logError", "notifyParent"],
                    on: { LOAD: "loading.firstLoad" },
                },
            },
        },
        {
            actions: {
                sendResponse: send<Context, Events>((context, event) => {
                    if (!event.type.startsWith(ActionTypes.DoneInvoke)) {
                        throw new Error(
                            `Invariant violation: Expected event "${ActionTypes.DoneInvoke}" but instead got "${event.type}"`,
                        )
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const ev = event as DoneInvokeEvent<any>
                    if (ev.data.type === successType) return parseResponse(Success, ev, "LOAD_SUCCESS")
                    if (ev.data.type === errorType) return parseResponse(ErrorM, ev, "LOAD_ERROR")
                    if (ev.data.type === BadRequestResponse.strictType.name) return parseBadRequestResponse(ev)
                    return unknownResponseTypeEvent(ev.data.type, ev)
                }),
                sendFatalError: send<Context, Events>((_, event): LoadFatalError => {
                    if (!event.type.startsWith(ActionTypes.ErrorPlatform)) {
                        throw new Error(
                            `Invariant violation: Expected event "${ActionTypes.ErrorPlatform}" but instead got "${event.type}"`,
                        )
                    }
                    //NOTICE currently the only chance to get here is when the WS closes. Therefore, this case can be
                    // interpreted as "WS closed" (see WebSocketClient).
                    // In case this would change, we should review how we send the info to data-load-machine clients.
                    const ev = event as DoneInvokeEvent<unknown>
                    return {
                        type: "LOAD_FATAL_ERROR",
                        errorCode: "connectionClosed",
                        message: `${ev.data}`,
                        info: ev.data,
                    }
                }),
                setResponse: assign<Context, Events>({
                    response: (_, ev) => {
                        assertEvent(ev, "LOAD_SUCCESS")
                        return ev.response
                    },
                }),
                notifyParent: actions.pure<Context, Events>((ctx, ev) => {
                    const parentEvent = notifyParent(ctx, ev)
                    return parentEvent ? sendParent(parentEvent) : undefined
                }),
                setError: assign<Context, Events>({
                    error: (_, ev) => {
                        assertEvent(ev, "LOAD_ERROR")
                        return { type: "ERROR", error: ev.response }
                    },
                }),
                setFatalError: assign<Context, Events>({
                    error: (_, ev) => {
                        assertEvent(ev, "LOAD_FATAL_ERROR")
                        return { type: "FATAL", errorCode: ev.errorCode, message: ev.message, info: ev.info }
                    },
                }),
                clearError: assign<Context, Events>({
                    error: undefined,
                }),
                logMe: logIfEnabled(actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev }))),
                logError: logIfEnabled(
                    actions.log<Context, Events>((ctx, ev) => ({ id, type: "LOADING ERROR", ctx, ev })),
                ),
            },
            services: {
                sendRequest: async (context, event) => {
                    assertEvent(event, "LOAD")
                    // return channel.sendAndReceive({
                    //     country: event.country,
                    //     action: action,
                    //     data: Request.strictType.encode(event.data),
                    // })

                    const start = Date.now()
                    try {
                        const result = await channel.sendAndReceive({
                            country: event.country,
                            action: action,
                            data: Request.strictType.encode(event.data),
                        })
                        const type = result.type === successType ? "success" : "response-error"
                        trackApiPerf(toTrackingData(action, elapsedInMs(start), type, context, event))
                        return result
                    } catch (err) {
                        trackApiPerf(toTrackingData(action, elapsedInMs(start), "network-error", context, event))
                        throw err
                    }
                },
            },
        },
    )

    function toTrackingData(
        action: string,
        elapsedInMs: number,
        type: PerfTrackingData["type"],
        ctx: Context,
        ev: Types["Load"],
    ) {
        return {
            action,
            apiId: ctx.id,
            elapsedInMs: elapsedInMs,
            country: ev.country,
            type,
        }
    }
}
